---
title: "Inwentaryzacja powykonawcza — co to jest i co musi zawierać"
description: "Inwentaryzacja powykonawcza co to jest — obowiązkowy dokument geodezyjny po zakończeniu budowy: zakres, zawartość, kto sporządza i kiedy jest wymagana."
date: "2026-06-07"
slug: "inwentaryzacja-powykonawcza-co-zawiera"
faq:
  - q: "Czy inwentaryzacja powykonawcza jest zawsze obowiązkowa?"
    a: "Tak, dla wszystkich obiektów wymagających pozwolenia na użytkowanie lub zawiadomienia o zakończeniu budowy. Dla drobnych robót budowlanych niewymagających pozwolenia może nie być konieczna — sprawdź z geodetą i nadzorem budowlanym."
  - q: "Ile czasu trwa wykonanie inwentaryzacji powykonawczej?"
    a: "Od kilku dni do 3 tygodni, zależnie od złożoności obiektu i obciążenia ODGiK. Termin przyjęcia operatu przez ODGiK bywa wąskim gardłem — warto zlecić geodecie jak najwcześniej."
  - q: "Czy inwentaryzację powykonawczą można zrobić samemu?"
    a: "Nie. Wymagane są uprawnienia zawodowe geodety. Próba sporządzenia operatu bez uprawnień jest nielegalna i taki dokument nie zostanie przyjęty przez ODGiK."
  - q: "Co to jest ODGiK?"
    a: "Ośrodek Dokumentacji Geodezyjnej i Kartograficznej — starostwiański urząd przyjmujący operaty geodezyjne, nadający im numery i przechowujący je w państwowym zasobie. Każde powiatowe starostwo prowadzi własny ODGiK."
  - q: "Czym różni się inwentaryzacja powykonawcza od mapy do celów projektowych?"
    a: "Mapa do celów projektowych to mapa sporządzana przed budową na potrzeby projektu. Inwentaryzacja powykonawcza jest sporządzana po budowie i dokumentuje stan rzeczywisty. Oba dokumenty muszą sporządzić uprawnieni geodeci."
---

Inwentaryzacja powykonawcza co to jest — to pytanie, które pojawia się przy każdym odbiorze budynku lub inwestycji liniowej. Jest to obowiązkowy dokument geodezyjny, bez którego nie uzyskasz pozwolenia na użytkowanie. Poniżej wyjaśniamy, czym jest inwentaryzacja powykonawcza, kto ją sporządza i co musi zawierać.

---

## Czym jest inwentaryzacja powykonawcza?

**Inwentaryzacja powykonawcza** (geodezyjna inwentaryzacja powykonawcza) to pomiar geodezyjny wykonany po zakończeniu budowy lub etapu robót, dokumentujący **rzeczywiste położenie** i wymiary wybudowanego obiektu lub sieci. Jest to obowiązkowy element procesu budowlanego, wymagany przez art. 57 ust. 1 pkt 5 ustawy Prawo budowlane jako załącznik do zawiadomienia o zakończeniu budowy lub wniosku o pozwolenie na użytkowanie.

Celem inwentaryzacji jest porównanie stanu zbudowanego ze stanem projektowanym — i wprowadzenie wyników do państwowego zasobu geodezyjnego i kartograficznego (bazy danych EGiB i BDOT).

---

## Kto sporządza inwentaryzację powykonawczą?

Inwentaryzację powykonawczą może sporządzić wyłącznie **uprawniony geodeta** — osoba z uprawnieniami zawodowymi w dziedzinie geodezji i kartografii (uprawnienia do wykonywania samodzielnych funkcji w dziedzinie geodezji, zakres 1 lub 2 zgodnie z art. 43 ustawy Prawo geodezyjne i kartograficzne).

Geodeta wykonuje pomiary w terenie, opracowuje wyniki i przekazuje operat do ośrodka dokumentacji geodezyjnej i kartograficznej (ODGiK), który nadaje mu numer i przyjmuje do zasobu.

---

## Co zawiera inwentaryzacja powykonawcza?

### Część polowa (pomiarowa)

- Szkice polowe z pomiarami terenu.
- Wyniki pomiaru położenia wybudowanych obiektów (współrzędne narożników, osi, węzłów).
- Pomiary wysokościowe (rzędne posadzki, kalenicy, fundamentów).

### Część kameralna (opracowanie)

- **Mapa inwentaryzacji powykonawczej** — aktualizacja mapy zasadniczej z naniesionymi wybudowanymi obiektami i sieciami.
- Zestawienie porównujące wymiary projektowane z wymiarami rzeczywistymi (odchyłki).
- Opis techniczny operatu.
- Protokół przyjęcia operatu przez ODGiK (klauzula).

### Co konkretnie jest inwentaryzowane?

Zakres inwentaryzacji zależy od rodzaju robót:

**Budynki:**
- obrys budynku (ławy i ściany fundamentowe, rzut parteru),
- rzędna posadzki parteru,
- ewentualnie: położenie garaży podziemnych, przekroje.

**Sieci uzbrojenia terenu:**
- przebieg kanalizacji, wodociągu, gazociągu, kabli energetycznych, teletechnicznych,
- studnie, zawory, złącza kablowe,
- rzędne posadowienia (głębokość).

**Drogi i place:**
- krawędzie jezdni i chodników,
- obiekty małej architektury,
- miejsca parkingowe, zieleń (w zależności od wymagań).

---

## Kiedy wymagana jest inwentaryzacja powykonawcza?

Inwentaryzacja powykonawcza jest wymagana:

1. **Przy wniosku o pozwolenie na użytkowanie** — jako obowiązkowy załącznik (art. 57 ust. 1 pkt 5 Prawa budowlanego).
2. **Przy zawiadomieniu o zakończeniu budowy** — dla obiektów, dla których wymagana jest inwentaryzacja.
3. **Po każdym etapie robót związanych z sieciami** — przed zasypaniem wykopów z sieciami podziemnymi (inwentaryzacja bieżąca).

Brak inwentaryzacji powykonawczej uniemożliwia uzyskanie pozwolenia na użytkowanie i — w konsekwencji — legalną eksploatację obiektu.

---

## Inwentaryzacja powykonawcza a inwentaryzacja usterek — ważna różnica

**Inwentaryzacja powykonawcza** to termin ściśle geodezyjny — pomiar rzeczywistego stanu po budowie. Nie należy mylić jej z **inwentaryzacją usterek** (snagging), która jest procesem dokumentowania wad i niezgodności wykonawczych.

| | Inwentaryzacja powykonawcza | Inwentaryzacja usterek |
|---|---|---|
| Charakter | Geodezyjny dokument urzędowy | Techniczna lista niezgodności |
| Kto wykonuje | Uprawniony geodeta | Inspektor nadzoru, GW, nabywca |
| Podstawa prawna | Art. 57 Prawa budowlanego | Rękojmia, gwarancja, umowa |
| Cel | Aktualizacja zasobu geodezyjnego | Naprawienie wad przed odbiorem |

---

## Terminy i koszty

Wykonanie inwentaryzacji powykonawczej trwa zazwyczaj od kilku dni do 3 tygodni, w zależności od wielkości obiektu i obciążenia ODGiK. Koszty:

- mały budynek mieszkalny jednorodzinny: ok. 500–1200 zł,
- budynek wielorodzinny lub komercyjny: 1500–5000 zł,
- rozbudowana sieć uzbrojenia: od 500 zł/km.

Cena obejmuje pomiar w terenie, opracowanie operatu i opłatę za przyjęcie do ODGiK.

---

## Jak Pinveo wspiera dokumentację przy inwestycjach budowlanych?

Pinveo nie jest narzędziem geodezyjnym i nie zastępuje inwentaryzacji powykonawczej — ta musi być sporządzona przez uprawnionego geodetę. Pinveo wspiera natomiast **równoległą dokumentację techniczną** podczas budowy i odbioru:

- **Markery na rzucie PDF** — oznaczanie usterek, niezgodności z projektem i etapów robót na planie kondygnacji.
- **Zdjęcia z GPS** — dokumentacja stanu robót ukrytych (zbrojenie, instalacje) przed zakryciem.
- **Formularze odbioru robót** — protokoły poszczególnych etapów z listą sprawdzonych elementów.
- **Raport PDF i Excel** — dokumentacja odbioru dostępna dla inwestora, GW i nadzoru.
- **Praca offline** — w nowym budynku często brak zasięgu, Pinveo działa bez internetu.

Dowiedz się więcej o dokumentacji instalacji: [Instalacje i geodezja z Pinveo](/zastosowania/instalacje-geodezja).

Przeczytaj też: [Snagging — czym jest lista usterek i jak ją prowadzić na budowie](/blog/snagging-co-to-jest-lista-usterek).

---

## Podsumowanie

Inwentaryzacja powykonawcza to obowiązkowy geodezyjny pomiar powykonawczy dokumentujący rzeczywiste położenie i wymiary wybudowanego obiektu lub sieci. Musi ją sporządzić uprawniony geodeta, a operat jest wymagany przy wniosku o pozwolenie na użytkowanie. Nie należy mylić jej z inwentaryzacją usterek (snaggingiem). Pinveo wspiera równoległą dokumentację techniczną — protokoły etapów, zdjęcia z GPS i markery na rzutach — ale nie zastępuje dokumentacji geodezyjnej.

---

## FAQ

### Czy inwentaryzacja powykonawcza jest zawsze obowiązkowa?

Tak, dla wszystkich obiektów wymagających pozwolenia na użytkowanie lub zawiadomienia o zakończeniu budowy. Dla drobnych robót budowlanych niewymagających pozwolenia może nie być konieczna — sprawdź z geodetą i nadzorem budowlanym.

### Ile czasu trwa wykonanie inwentaryzacji powykonawczej?

Od kilku dni do 3 tygodni, zależnie od złożoności obiektu i obciążenia ODGiK. Termin przyjęcia operatu przez ODGiK bywa wąskim gardłem — warto zlecić geodecie jak najwcześniej.

### Czy inwentaryzację powykonawczą można zrobić samemu?

Nie. Wymagane są uprawnienia zawodowe geodety. Próba sporządzenia operatu bez uprawnień jest nielegalna i taki dokument nie zostanie przyjęty przez ODGiK.

### Co to jest ODGiK?

Ośrodek Dokumentacji Geodezyjnej i Kartograficznej — starostwiański urząd przyjmujący operaty geodezyjne, nadający im numery i przechowujący je w państwowym zasobie. Każde powiatowe starostwo prowadzi własny ODGiK.

### Czym różni się inwentaryzacja powykonawcza od mapy do celów projektowych?

Mapa do celów projektowych to mapa sporządzana **przed** budową na potrzeby projektu. Inwentaryzacja powykonawcza jest sporządzana **po** budowie i dokumentuje stan rzeczywisty. Oba dokumenty muszą sporządzić uprawni geodeci.
